import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';

import {
  MetricCardBlock,
  WidgetTone
} from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-metric-card-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    <div class="mc" [attr.data-tone]="block.tone || 'neutral'">
      @if (block.icon) {
        <mat-icon class="mc__icon">{{ block.icon }}</mat-icon>
      }
      <div class="mc__body">
        <div class="mc__label">{{ block.label }}</div>
        <div class="mc__value">{{ block.value }}</div>
        @if (block.hint) {
          <div class="mc__hint">{{ block.hint }}</div>
        }
      </div>
      @if (block.trend) {
        <div class="mc__trend" [attr.data-tone]="trendTone()">
          <mat-icon>{{ trendIcon() }}</mat-icon>
          <span>{{ block.trend.label }}</span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .mc {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        border-radius: 14px;
        background: rgb(var(--ds-surface));
        border: 1px solid rgb(var(--ds-text) / 0.08);
        border-left: 3px solid rgb(var(--ds-interactive));
      }
      .mc[data-tone='positive'] {
        border-left-color: rgb(var(--ds-success));
      }
      .mc[data-tone='warning'] {
        border-left-color: rgb(var(--ds-warning));
      }
      .mc[data-tone='danger'] {
        border-left-color: rgb(var(--ds-danger));
      }
      .mc__icon {
        font-size: 28px;
        width: 28px;
        height: 28px;
        color: rgb(var(--ds-interactive));
        flex-shrink: 0;
      }
      .mc[data-tone='positive'] .mc__icon {
        color: rgb(var(--ds-success));
      }
      .mc[data-tone='warning'] .mc__icon {
        color: rgb(var(--ds-warning));
      }
      .mc[data-tone='danger'] .mc__icon {
        color: rgb(var(--ds-danger));
      }
      .mc__body {
        flex: 1;
        min-width: 0;
      }
      .mc__label {
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgb(var(--ds-text) / 0.55);
        overflow-wrap: anywhere;
      }
      .mc__value {
        font-size: 26px;
        font-weight: 700;
        line-height: 1.1;
        margin-top: 2px;
        color: rgb(var(--ds-text) / 0.92);
        overflow-wrap: anywhere;
      }
      .mc__hint {
        margin-top: 4px;
        font-size: 12px;
        color: rgb(var(--ds-text) / 0.55);
        overflow-wrap: anywhere;
      }
      .mc__trend {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgb(var(--ds-interactive) / 0.1);
        color: rgb(var(--ds-interactive));
        font-weight: 600;
        font-size: 13px;
        flex-shrink: 0;
        white-space: nowrap;
      }
      .mc__trend[data-tone='positive'] {
        background: rgb(var(--ds-success) / 0.12);
        color: rgb(var(--ds-success));
      }
      .mc__trend[data-tone='warning'] {
        background: rgb(var(--ds-warning) / 0.14);
        color: rgb(var(--ds-warning));
      }
      .mc__trend[data-tone='danger'] {
        background: rgb(var(--ds-danger) / 0.12);
        color: rgb(var(--ds-danger));
      }
      .mc__trend mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    `
  ]
})
export class AiMetricCardBlockComponent {
  @Input({ required: true }) block!: MetricCardBlock;

  trendIcon(): string {
    if (!this.block.trend) return '';
    if (this.block.trend.direction === 'up') return 'arrow_upward';
    if (this.block.trend.direction === 'down') return 'arrow_downward';
    return 'remove';
  }

  trendTone(): WidgetTone {
    const t = this.block.trend;
    if (!t) return 'neutral';
    if (t.tone) return t.tone;
    if (t.direction === 'up') return 'positive';
    if (t.direction === 'down') return 'danger';
    return 'neutral';
  }
}
